import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type Cell,
  ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  type RowData,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronRight, GripVertical, PanelRight, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Scrollbar } from '@/components/Scrollbar';
import {
  Button,
  Card,
  Checkbox,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  TruncatedText,
} from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import { Pagination } from '../pagination';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Override horizontal alignment for the column header and body cells. */
    align?: 'left' | 'right' | 'center';
    /** Extra classes merged onto the body `<td>` for this column (e.g. sticky styling). */
    cellClassName?: string;
    /** Extra classes merged onto the header `<th>` for this column. */
    headerClassName?: string;
  }
}

/**
 * Trailing-cue affordance for a row: indicates what clicking the row does.
 * `'route'` → navigates to another view (ChevronRight glyph);
 * `'sheet'` → opens a side sheet/drawer (PanelRight glyph).
 */
export type RowAffordance = 'route' | 'sheet';

const DEFAULT_SCROLLABLE_TARGET = 'sidebar-template-scrolleable-container';
const DEFAULT_LOADING_MORE_ROW_COUNT = 3;
const DEFAULT_MIN_ITEMS_TO_FILL = 10;
const DEFAULT_SCROLL_THRESHOLD = 0.9;

/**
 * Infinite-scroll mode for `DataTable`. When provided on `DataTableProps.infinite`,
 * the table swaps its pagination footer for an IntersectionObserver-driven
 * sentinel and renders a skeleton-row "loading more" footer inside `<TableBody>`.
 *
 * The consumer owns the `useInfiniteQuery` and passes the **flattened** results as
 * `data` (e.g. `pages.flatMap((p) => p.results)`). The variant works with all
 * pagination shapes (page-based, cursor, total-count) — it only reads the three
 * standard `useInfiniteQuery` flags below.
 *
 * Sorting in this mode is API-driven only: the table fires `onSortingChange` for
 * the consumer to wire to backend sort params, but does NOT sort client-side
 * (which would silently sort only the rows fetched so far).
 *
 * Mutually exclusive with `manualPagination` — both being set logs a dev warning
 * and `manualPagination` is ignored.
 */
export interface DataTableInfiniteProps {
  /** From `useInfiniteQuery`. */
  hasNextPage: boolean;
  /** From `useInfiniteQuery`. */
  isFetchingNextPage: boolean;
  /** Triggered when the user scrolls past the threshold. */
  fetchNextPage: () => void | Promise<unknown>;
  /**
   * ID of the DOM element that owns the scroll. Defaults to
   * `'sidebar-template-scrolleable-container'` — the SidebarTemplate's scroller,
   * used by every existing in-app InfiniteScroll caller. Pass `null` to use
   * window scroll (e.g. for Storybook or non-SidebarTemplate hosts).
   */
  scrollableTarget?: string | null;
  /** Skeleton rows shown at the bottom while fetching the next page. Defaults to 3. */
  loadingMoreRowCount?: number;
  /** Footer message when `hasNextPage` is false. Pass `false` to suppress. */
  endMessage?: React.ReactNode | false;
  /** Render a "Load more" button as a fallback for scroll detection. Defaults to false. */
  manualLoadMore?: boolean;
  /**
   * Auto-fetch additional pages until the table holds at least this many rows
   * (the "viewport doesn't fill" auto-fill bug from `useInfiniteScrollFix`).
   * Defaults to 10. Pass 0 to disable.
   */
  minItemsToFill?: number;
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  loading?: boolean;
  // Sorting
  initialSorting?: SortingState;
  /**
   * Controlled sorting state. When provided, the table runs in fully controlled
   * mode: the visible arrow indicator always reflects this prop, and TanStack
   * does NOT mutate any internal sort state. Pair with `onSortingChange` so the
   * parent (typically `nuqs` URL state) owns the source of truth.
   *
   * Mutually exclusive with `initialSorting`. Required when the consumer wants
   * the arrow to track URL/external state across re-renders — `initialSorting`
   * is one-shot and drifts after the first click.
   */
  sortingState?: SortingState;
  enableSorting?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  // Pagination
  pageSizeOptions?: number[];
  initialPageSize?: number;
  // Server-side pagination
  manualPagination?: boolean;
  rowCount?: number;
  paginationState?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  /**
   * Infinite-scroll mode. When provided, the table replaces the pagination footer
   * with a scroll listener on the scrollable target and renders a skeleton-row
   * "loading more" footer. See {@link DataTableInfiniteProps}.
   */
  infinite?: DataTableInfiniteProps;
  // Row ID
  getRowId?: (row: TData) => string;
  /**
   * Enable row selection. When `true`, a checkbox column is rendered as the
   * first column (with a "select all" checkbox in the header). Pair with
   * `rowSelection` + `onRowSelectionChange` for controlled selection state,
   * and `bulkActions` to render bulk-action buttons in the toolbar.
   *
   * Pass a predicate `(row) => boolean` to gate selection per-row — rows that
   * return `false` render no checkbox and are skipped by "select all". Useful
   * when the bulk-action endpoint requires a uniform shape across the
   * selection (e.g. all rows must share a status).
   *
   * Pass `getRowId` so selection state survives row re-orders / pagination.
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  /** Controlled selection state. Map of `rowId -> true`. */
  rowSelection?: RowSelectionState;
  /** Called whenever the selection set changes. */
  onRowSelectionChange?: (state: RowSelectionState) => void;
  /**
   * Buttons rendered in place of `actions` in the toolbar when at least one row
   * is selected. Typically contains "Approve selected", "Delete selected", etc.
   */
  bulkActions?: React.ReactNode;
  /**
   * Enable drag-and-drop row reordering. When `true`, a drag-handle column is
   * rendered as the first column (right of the selection column when both are
   * on) and rows can be reordered by drag or keyboard. Pair with `onRowReorder`
   * to receive the new ordering.
   *
   * Requires `getRowId` — `@dnd-kit` keys sortable items by ID, so without a
   * stable ID the drag operations target the wrong rows. Mutually unfriendly
   * with `manualPagination`, `infinite`, and column sorting (`enableSorting`):
   * each one fights with the user-defined order. Dev-only `console.warn` if
   * combined.
   */
  enableRowReorder?: boolean;
  /**
   * Called with the new row ordering when the user drops a row. Receives the
   * full reordered `data` array — wire to a mutation that persists the new
   * order. Required when `enableRowReorder` is true.
   */
  onRowReorder?: (newOrder: TData[]) => void;
  // Expandable rows
  /**
   * Enable per-row expansion. Return `true` for rows that can expand to reveal
   * a nested detail panel rendered by `renderSubRow`. When provided, a synthetic
   * chevron column is rendered at the far left (only painted for rows that can
   * expand). Expansion is client-only UI state, so it composes with
   * `manualPagination` and `manualSorting`. Mutually unfriendly with
   * `enableRowReorder` (dev-only `console.warn` if combined).
   *
   * Opt-in: when omitted, no expander column is rendered and behavior is
   * unchanged.
   */
  getRowCanExpand?: (row: Row<TData>) => boolean;
  /**
   * Renders the expanded detail panel for a row. Called with `row.original`
   * when the row is expanded; the returned node is placed in a full-width
   * second `<tr>` spanning every visible column. Required for expansion to
   * show anything — pair with `getRowCanExpand`.
   */
  renderSubRow?: (row: TData) => React.ReactNode;
  // Toolbar
  /** Called when the user clicks the refresh button. */
  onRefresh?: () => void;
  /** Show a spinner on the refresh button while data is being fetched. */
  isRefreshing?: boolean;
  /** Extra elements rendered next to the refresh button (e.g. Export CSV, Import CSV). */
  actions?: React.ReactNode;
  /** Custom label rendered on the left side of the toolbar. Defaults to "Showing X of Y results". */
  resultsLabel?: React.ReactNode;
  /**
   * Filter controls rendered at the top of the Card, above the toolbar. Typically
   * a `<FilterToolbar>` instance. The DataTable does not own filter state — the
   * consumer owns it (e.g. via `nuqs`/Zustand) and feeds the filtered `data`.
   */
  filters?: React.ReactNode;
  /**
   * Render without the built-in `Card` wrapper. Use when embedding `DataTable` inside an
   * outer `Card` that already provides a `CardHeader` (e.g. tables that need a section
   * title above the toolbar). Defaults to `false`.
   */
  bare?: boolean;
  /**
   * Called when a row is clicked. When provided, rows render with a pointer cursor and
   * the `tr` becomes the click target. Cells with their own interactive elements should
   * call `event.stopPropagation()` to avoid double-navigation.
   */
  onRowClick?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /**
   * Trailing sticky cue indicating what clicking a row does. Pass a literal when the
   * whole table behaves the same (`'route'` or `'sheet'`), or a function for mixed
   * tables (return `undefined` to render no glyph for that row). Purely indicative —
   * it does not change click behavior; wire the action via `onRowClick`.
   */
  rowAffordance?: RowAffordance | ((row: TData) => RowAffordance | undefined);
  /**
   * Per-row className. Receives the TanStack `Row` so the consumer can read
   * `row.original`, `row.getIsSelected()`, etc. Useful for surfacing row state
   * (e.g. a red tint on rows that were already processed by a prior bulk action,
   * or a primary tint on selected rows when row-click selects).
   */
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /**
   * Custom empty-state content shown when `data` is empty and `loading` is false.
   * Defaults to a plain "No rows" cell.
   */
  emptyState?: React.ReactNode;
  /**
   * Wrap the table in a horizontally-scrollable container. Use for wide tables
   * (many columns) so they overflow horizontally instead of clipping at the Card edge.
   */
  scrollable?: boolean;
  /** Forwarded to the inner `<Table>` element. Useful for `tw:whitespace-nowrap`. */
  tableClassName?: string;
  className?: string;
}

export function DataTable<TData>({
  columns,
  data,
  loading = false,
  initialSorting,
  sortingState,
  enableSorting = true,
  onSortingChange,
  pageSizeOptions = [25, 50, 100],
  initialPageSize = 25,
  manualPagination = false,
  rowCount,
  paginationState,
  onPaginationChange,
  infinite,
  getRowId,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  bulkActions,
  enableRowReorder = false,
  onRowReorder,
  getRowCanExpand,
  renderSubRow,
  onRefresh,
  isRefreshing = false,
  actions,
  resultsLabel,
  filters,
  bare = false,
  onRowClick,
  rowAffordance,
  getRowClassName,
  emptyState,
  scrollable = false,
  tableClassName,
  className,
}: DataTableProps<TData>) {
  const isInfinite = !!infinite;
  const isExpandable = !!getRowCanExpand;

  if (process.env.NODE_ENV !== 'production' && isExpandable && enableRowReorder) {
    console.warn(
      '[DataTable] `getRowCanExpand` (expandable rows) and `enableRowReorder` both add a leading control column and fight over the same row interactions; do not combine them.',
    );
  }

  if (process.env.NODE_ENV !== 'production' && isInfinite && manualPagination) {
    console.warn(
      '[DataTable] `infinite` and `manualPagination` are mutually exclusive. `manualPagination` will be ignored.',
    );
  }

  if (process.env.NODE_ENV !== 'production' && scrollable && rowAffordance) {
    console.warn(
      '[DataTable] `scrollable` (SimpleBar) is ignored when `rowAffordance` is set so the sticky affordance column can pin to the viewport. The table still scrolls horizontally via its native scroll container.',
    );
  }

  if (process.env.NODE_ENV !== 'production' && enableRowReorder) {
    if (!getRowId) {
      console.warn('[DataTable] `enableRowReorder` requires `getRowId` so drag-and-drop targets stable row IDs.');
    }
    if (manualPagination) {
      console.warn(
        '[DataTable] `enableRowReorder` + `manualPagination` is ambiguous — reordering only sees the current page.',
      );
    }
    if (isInfinite) {
      console.warn('[DataTable] `enableRowReorder` is not supported with infinite scroll.');
    }
    if (enableSorting) {
      console.warn(
        '[DataTable] `enableRowReorder` + column sorting both modify row order; pass `enableSorting={false}` while reorder is active.',
      );
    }
  }

  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  const useManualPagination = manualPagination || isInfinite;
  const activePagination = manualPagination && !isInfinite ? paginationState! : internalPagination;

  // Controlled-sorting mode: when the consumer passes `sortingState`, the parent
  // owns the source of truth. TanStack reads from it and never mutates internal
  // state. Server-side sort is also auto-enabled when the consumer pairs
  // `manualPagination` with `onSortingChange` — client-side sort against a
  // partial page would silently disagree with the server result.
  const isControlledSorting = sortingState !== undefined;
  const useManualSorting = isInfinite || (manualPagination && !!onSortingChange) || isControlledSorting;
  const activeSorting = isControlledSorting ? sortingState : sorting;

  if (process.env.NODE_ENV !== 'production') {
    if (isControlledSorting && !onSortingChange) {
      console.warn('[DataTable] `sortingState` was passed without `onSortingChange`; clicks will not propagate.');
    }
    if (isControlledSorting && initialSorting) {
      console.warn(
        '[DataTable] `sortingState` and `initialSorting` are mutually exclusive; `initialSorting` is ignored.',
      );
    }
  }

  const selectionColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (!enableRowSelection) return null;
    return {
      id: '__select',
      enableSorting: false,
      enableResizing: false,
      size: 44,
      header: ({ table: t }) => (
        <Checkbox
          checked={t.getIsAllRowsSelected() ? true : t.getIsSomeRowsSelected() ? 'indeterminate' : false}
          onCheckedChange={(value) => t.toggleAllRowsSelected(!!value)}
          aria-label="Select all rows"
        />
      ),
      cell: ({ row }) =>
        row.getCanSelect() ? (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        ) : null,
    };
  }, [enableRowSelection]);

  const reorderColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (!enableRowReorder) return null;
    return {
      id: '__reorder',
      enableSorting: false,
      enableResizing: false,
      size: 40,
      header: () => null,
      cell: () => null,
    };
  }, [enableRowReorder]);

  const expanderColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (!getRowCanExpand) return null;
    return {
      id: '__expander',
      enableSorting: false,
      enableResizing: false,
      size: 40,
      header: () => null,
      cell: ({ row }) =>
        row.getCanExpand() ? (
          <Button
            variant="ghost"
            size="icon"
            className="tw:h-7 tw:w-7"
            aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
            aria-expanded={row.getIsExpanded()}
            onClick={row.getToggleExpandedHandler()}
          >
            <ChevronRight
              size={16}
              className={cn('tw:transition-transform', row.getIsExpanded() && 'tw:rotate-90')}
              aria-hidden="true"
            />
          </Button>
        ) : null,
    };
  }, [getRowCanExpand]);

  const affordanceColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (!rowAffordance) return null;
    const resolve = (row: TData): RowAffordance | undefined =>
      typeof rowAffordance === 'function' ? rowAffordance(row) : rowAffordance;
    return {
      id: '__affordance',
      enableSorting: false,
      enableResizing: false,
      size: 56,
      meta: {
        align: 'right',
        // A `sticky` cell must paint an OPAQUE background so other cells can't bleed
        // through while pinned. The row's hover/header tints are semi-transparent
        // (`bg-muted/50`, `bg-muted/30`) composited over the card — reproduce them as
        // solid `color-mix` colors so the sticky cell matches exactly, and add
        // `transition-colors` so it fades in sync with the row (which also uses it).
        // Two plain `bg-*` utilities can't be layered (one wins), which is why the
        // earlier `bg-card bg-muted/30` rendered the wrong grey.
        cellClassName:
          'tw:sticky tw:right-0 tw:z-[1] tw:transition-[background-color,box-shadow] tw:bg-card ' +
          'tw:group-hover/row:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--card)))] ' +
          'tw:group-hover/row:[box-shadow:inset_-3px_0_0_0_var(--tw-color-fuul-blue)] ' +
          // Hide the cue (rail) when a click would NOT trigger the row action — i.e. the
          // pointer is over an interactive child (checkbox, action button/menu, link, input)
          // that stops propagation. The glyph is suppressed the same way.
          'tw:group-has-[button:hover]/row:[box-shadow:none] tw:group-has-[a:hover]/row:[box-shadow:none] tw:group-has-[input:hover]/row:[box-shadow:none] ' +
          'tw:group-data-[state=selected]/row:bg-muted',
        headerClassName:
          'tw:sticky tw:right-0 tw:z-[1] tw:bg-[color-mix(in_srgb,hsl(var(--muted))_30%,hsl(var(--card)))]',
      },
      header: () => null,
      cell: ({ row }) => {
        const affordance = resolve(row.original);
        if (!affordance) return null;
        const Icon = affordance === 'route' ? ChevronRight : PanelRight;
        return (
          <span className="tw:flex tw:w-full tw:items-center tw:justify-end tw:text-fuul-blue tw:opacity-0 tw:-translate-x-1.5 tw:transition-[opacity,translate] tw:duration-[180ms] tw:ease-out tw:group-hover/row:opacity-100 tw:group-hover/row:translate-x-0 tw:group-has-[button:hover]/row:opacity-0 tw:group-has-[a:hover]/row:opacity-0 tw:group-has-[input:hover]/row:opacity-0">
            <Icon size={17} strokeWidth={1.6} aria-hidden="true" />
          </span>
        );
      },
    };
  }, [rowAffordance]);

  const tableColumns = useMemo<ColumnDef<TData>[]>(
    () =>
      [expanderColumn, reorderColumn, selectionColumn, ...columns, affordanceColumn].filter(
        Boolean,
      ) as ColumnDef<TData>[],
    [columns, expanderColumn, reorderColumn, selectionColumn, affordanceColumn],
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderableRowIds = useMemo(() => {
    if (!enableRowReorder) return [] as string[];
    return data.map((row, index) => (getRowId ? getRowId(row) : String(index)));
  }, [enableRowReorder, data, getRowId]);

  const handleReorderDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!enableRowReorder || !onRowReorder || !over || active.id === over.id) return;
    const oldIndex = reorderableRowIds.indexOf(String(active.id));
    const newIndex = reorderableRowIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onRowReorder(arrayMove(data, oldIndex, newIndex));
  };

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting: activeSorting,
      pagination: activePagination,
      ...(rowSelection !== undefined && { rowSelection }),
      ...(isExpandable && { expanded }),
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(activeSorting) : updater;
      if (!isControlledSorting) setSorting(next);
      onSortingChange?.(next);
    },
    onPaginationChange:
      manualPagination && !isInfinite
        ? (updater) => {
            const next = typeof updater === 'function' ? updater(paginationState!) : updater;
            onPaginationChange?.(next);
          }
        : setInternalPagination,
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(rowSelection ?? {}) : updater;
          onRowSelectionChange(next);
        }
      : undefined,
    onExpandedChange: isExpandable ? setExpanded : undefined,
    getRowCanExpand: isExpandable ? getRowCanExpand : undefined,
    getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,
    getCoreRowModel: getCoreRowModel(),
    // Server-side sort: trust the parent's data order, skip the client sort model.
    // Enabled whenever the data isn't fully local — infinite mode, controlled `sortingState`,
    // or `manualPagination + onSortingChange` (consumer wires sort to query params).
    getSortedRowModel: enableSorting && !useManualSorting ? getSortedRowModel() : undefined,
    manualSorting: useManualSorting ? true : undefined,
    getPaginationRowModel: !useManualPagination ? getPaginationRowModel() : undefined,
    manualPagination: useManualPagination,
    rowCount: manualPagination && !isInfinite ? rowCount : undefined,
    enableRowSelection,
    getRowId,
  });

  const selectedCount = useMemo(
    () => (rowSelection ? Object.values(rowSelection).filter(Boolean).length : 0),
    [rowSelection],
  );

  // Auto-fill once when the first page is too small to overflow the viewport,
  // so the user has something to scroll to and the scroll listener can engage.
  const hasTriggeredAutoFill = useRef(false);

  useEffect(() => {
    if (!infinite) return;
    const minFill = infinite.minItemsToFill ?? DEFAULT_MIN_ITEMS_TO_FILL;
    if (minFill === 0) return;
    if (!hasTriggeredAutoFill.current && !loading && infinite.hasNextPage && data.length > 0 && data.length < minFill) {
      hasTriggeredAutoFill.current = true;
      void infinite.fetchNextPage();
    }
  }, [infinite, loading, data.length]);

  useEffect(() => {
    if (data.length === 0) hasTriggeredAutoFill.current = false;
  }, [data.length]);

  const resolveScrollableTarget = (): string | undefined => {
    if (!infinite) return undefined;
    if (infinite.scrollableTarget === null) return undefined;
    return infinite.scrollableTarget ?? DEFAULT_SCROLLABLE_TARGET;
  };

  const fetchNextPageRef = useRef<DataTableInfiniteProps['fetchNextPage'] | undefined>(infinite?.fetchNextPage);
  fetchNextPageRef.current = infinite?.fetchNextPage;

  const isInfiniteScrollActive = !!isInfinite && !loading && !!infinite?.hasNextPage && !infinite?.isFetchingNextPage;
  const scrollableTargetId = resolveScrollableTarget();

  // Listen on both the named container and the window: depending on the host
  // template, one or the other is the actual scroller (ViewTemplate scrolls
  // the window, SidebarTemplate scrolls the container).
  useEffect(() => {
    if (!isInfiniteScrollActive) return;

    const targetEl = scrollableTargetId ? document.getElementById(scrollableTargetId) : null;

    const isElNearBottom = (el: HTMLElement) => {
      if (el.scrollHeight <= el.clientHeight) return false;
      const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
      return remaining <= el.clientHeight * (1 - DEFAULT_SCROLL_THRESHOLD);
    };

    const isWindowNearBottom = () => {
      const doc = document.documentElement;
      if (doc.scrollHeight <= window.innerHeight) return false;
      const remaining = doc.scrollHeight - (window.scrollY + window.innerHeight);
      return remaining <= window.innerHeight * (1 - DEFAULT_SCROLL_THRESHOLD);
    };

    const onScroll = () => {
      const nearBottom = (targetEl && isElNearBottom(targetEl)) || isWindowNearBottom();
      if (nearBottom) fetchNextPageRef.current?.();
    };

    targetEl?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      targetEl?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isInfiniteScrollActive, scrollableTargetId]);

  const currentPageIndex = table.getState().pagination.pageIndex;
  const currentPageSize = table.getState().pagination.pageSize;
  const totalRows = manualPagination && !isInfinite ? (rowCount ?? 0) : data.length;
  const visibleRows = table.getRowModel().rows.length;

  // Range label like "Showing 26–50 of 200" — without this, a paginated table
  // shows the same "Showing 25 of 200" on every page, which doesn't tell the
  // user where in the dataset they are.
  const rangeStart = currentPageIndex * currentPageSize + 1;
  const rangeEnd = Math.min(rangeStart + visibleRows - 1, totalRows);
  const rangeLabel = rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`;

  const hasSelection = selectedCount > 0;
  const defaultLabel = hasSelection
    ? `Selected ${selectedCount} ${selectedCount === 1 ? 'item' : 'items'}`
    : isInfinite
      ? `Showing ${visibleRows} results`
      : visibleRows === 0
        ? `Showing 0 of ${totalRows} results`
        : `Showing ${rangeLabel} of ${totalRows} results`;
  // When bare, only render the toolbar if the consumer asked for at least one slot.
  const showToolbar = bare ? !!resultsLabel || !!actions || !!onRefresh || !!bulkActions : true;

  const toolbar = showToolbar && (
    <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-4 tw:px-4 tw:py-3">
      {loading ? (
        <Skeleton variant="text" width={180} height={20} />
      ) : (
        <Text as="span" variant="body-md" weight="medium" color={hasSelection ? 'primary' : 'default'}>
          {resultsLabel ?? defaultLabel}
        </Text>
      )}
      <div className="tw:flex tw:items-center tw:gap-2">
        {hasSelection && bulkActions ? bulkActions : actions}
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing} aria-label="Refresh">
            {isRefreshing ? <Spinner size="sm" /> : <RefreshCw size={16} />}
          </Button>
        )}
      </div>
    </div>
  );

  const tableElement = (
    <Table className={tableClassName}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header, headerIndex) => {
              const canSort = enableSorting && header.column.getCanSort();
              const sortDirection = header.column.getIsSorted();
              // The trailing `__affordance` column aligns itself; treat the column
              // before it as the last content column so its right-align default holds.
              const isLastColumn =
                headerIndex === headerGroup.headers.length - 1 ||
                headerGroup.headers[headerIndex + 1]?.column.id === '__affordance';
              const metaAlign = header.column.columnDef.meta?.align;
              const align = metaAlign ?? (isLastColumn ? 'right' : 'left');
              const headerDef = header.column.columnDef.header;

              return (
                <TableHead
                  key={header.id}
                  onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  className={cn(
                    'tw:normal-case tw:text-sm tw:tracking-normal tw:transition-colors',
                    canSort && 'tw:cursor-pointer tw:select-none tw:hover:bg-muted/50 tw:hover:text-foreground',
                    align === 'right' && 'tw:text-right',
                    align === 'center' && 'tw:text-center',
                    header.column.columnDef.meta?.headerClassName,
                  )}
                  style={header.getSize() !== 150 ? { width: header.getSize() } : undefined}
                >
                  {header.isPlaceholder ? null : (
                    <span
                      className={cn(
                        'tw:flex tw:w-full tw:min-w-0 tw:items-center tw:gap-1',
                        align === 'right' && 'tw:justify-end',
                        align === 'center' && 'tw:justify-center',
                      )}
                    >
                      {typeof headerDef === 'string' ? (
                        <TruncatedText className="tw:min-w-0">{headerDef}</TruncatedText>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                      {sortDirection === 'asc' && <span aria-hidden="true">↑</span>}
                      {sortDirection === 'desc' && <span aria-hidden="true">↓</span>}
                    </span>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {loading ? (
          Array.from({ length: currentPageSize }).map((_, rowIndex) => (
            <TableRow key={`skeleton-${rowIndex}`}>
              {tableColumns.map((column, colIndex) => {
                const metaAlign = column.meta?.align;
                const isLast =
                  colIndex === tableColumns.length - 1 || tableColumns[colIndex + 1]?.id === '__affordance';
                const align = metaAlign ?? (isLast ? 'right' : 'left');
                return (
                  <TableCell
                    key={`skeleton-${rowIndex}-${colIndex}`}
                    className={cn(
                      align === 'right' && 'tw:text-right',
                      align === 'center' && 'tw:text-center',
                      column.meta?.cellClassName,
                    )}
                  >
                    <Skeleton variant="text" />
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : visibleRows === 0 ? (
          <TableRow>
            <TableCell colSpan={tableColumns.length} className="tw:p-0 tw:text-center tw:text-muted-foreground">
              {emptyState ?? <div className="tw:py-6">No rows</div>}
            </TableCell>
          </TableRow>
        ) : (
          renderRows({
            rows: table.getRowModel().rows,
            enableRowReorder,
            reorderableRowIds,
            sensors: dndSensors,
            onDragEnd: handleReorderDragEnd,
            onRowClick,
            getRowClassName,
            renderSubRow,
          })
        )}
        {isInfinite && infinite.isFetchingNextPage && !loading
          ? Array.from({ length: infinite.loadingMoreRowCount ?? DEFAULT_LOADING_MORE_ROW_COUNT }).map(
              (_, rowIndex) => (
                <TableRow key={`loading-more-${rowIndex}`}>
                  {tableColumns.map((column, colIndex) => {
                    const metaAlign = column.meta?.align;
                    const isLast =
                      colIndex === tableColumns.length - 1 || tableColumns[colIndex + 1]?.id === '__affordance';
                    const align = metaAlign ?? (isLast ? 'right' : 'left');
                    return (
                      <TableCell
                        key={`loading-more-${rowIndex}-${colIndex}`}
                        className={cn(
                          align === 'right' && 'tw:text-right',
                          align === 'center' && 'tw:text-center',
                          column.meta?.cellClassName,
                        )}
                      >
                        <Skeleton variant="text" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ),
            )
          : null}
      </TableBody>
    </Table>
  );

  // The `rowAffordance` column uses `position: sticky; right: 0`, which binds to the
  // nearest scroll container. SimpleBar (`scrollable`) nests a second scroll container
  // around the `Table` primitive's own `overflow-auto` wrapper; the sticky cell then
  // binds to that inner wrapper (which doesn't scroll inside SimpleBar) and gets parked
  // off-screen at the content's right edge. So when an affordance column is present we
  // skip SimpleBar and let the `Table` primitive's native scroller own horizontal scroll
  // — that scroller pins the sticky cue to the viewport correctly.
  const tableContent = scrollable && !rowAffordance ? <Scrollbar>{tableElement}</Scrollbar> : tableElement;

  const infiniteFooter = isInfinite && (
    <>
      {!infinite.hasNextPage && data.length > 0 && infinite.endMessage !== false && (
        <div className="tw:flex tw:items-center tw:justify-center tw:py-4">
          {infinite.endMessage ?? (
            <Text variant="body-md" color="muted">
              No more items to load
            </Text>
          )}
        </div>
      )}
      {infinite.manualLoadMore && infinite.hasNextPage && !infinite.isFetchingNextPage && (
        <div className="tw:flex tw:items-center tw:justify-center tw:py-4">
          <Button variant="outline" size="sm" onClick={() => void infinite.fetchNextPage()}>
            Load more
          </Button>
        </div>
      )}
    </>
  );

  const filtersSlot = filters ? <div className="tw:px-4 tw:pt-4">{filters}</div> : null;

  const body = (
    <>
      {filtersSlot}
      {toolbar}
      {tableContent}
      {isInfinite ? (
        infiniteFooter
      ) : (
        <Pagination
          pageIndex={currentPageIndex}
          pageSize={currentPageSize}
          totalRows={totalRows}
          pageSizeOptions={pageSizeOptions}
          onPageChange={(nextIndex) => table.setPageIndex(nextIndex)}
          onPageSizeChange={(nextSize) => table.setPageSize(nextSize)}
        />
      )}
    </>
  );

  if (bare) {
    return body;
  }

  return (
    <Card overflow="hidden" className={cn('tw:w-full', className)}>
      {body}
    </Card>
  );
}

// ─── Row rendering helpers ────────────────────────────────────────────────

interface RenderRowsArgs<TData> {
  rows: Row<TData>[];
  enableRowReorder: boolean;
  reorderableRowIds: string[];
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  onRowClick?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  renderSubRow?: (row: TData) => React.ReactNode;
}

function renderRows<TData>({
  rows,
  enableRowReorder,
  reorderableRowIds,
  sensors,
  onDragEnd,
  onRowClick,
  getRowClassName,
  renderSubRow,
}: RenderRowsArgs<TData>) {
  if (!enableRowReorder) {
    return rows.map((row) => (
      <DataTableRow
        key={row.id}
        row={row}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        renderSubRow={renderSubRow}
      />
    ));
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={reorderableRowIds} strategy={verticalListSortingStrategy}>
        {rows.map((row) => (
          <SortableDataRow key={row.id} row={row} onRowClick={onRowClick} getRowClassName={getRowClassName} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

interface DataRowProps<TData> {
  row: Row<TData>;
  onRowClick?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  renderSubRow?: (row: TData) => React.ReactNode;
}

function DataTableRow<TData>({ row, onRowClick, getRowClassName, renderSubRow }: DataRowProps<TData>) {
  const cells = row.getVisibleCells();
  const isExpanded = row.getIsExpanded();
  return (
    <>
      <TableRow
        data-state={row.getIsSelected() ? 'selected' : undefined}
        onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
        className={cn('tw:group/row', onRowClick && 'tw:cursor-pointer', getRowClassName?.(row))}
      >
        {cells.map((cell, cellIndex) => {
          const isExpanderCell = cell.column.id === '__expander';
          const metaAlign = cell.column.columnDef.meta?.align;
          const isLast = cellIndex === cells.length - 1 || cells[cellIndex + 1]?.column.id === '__affordance';
          const align = metaAlign ?? (isLast ? 'right' : 'left');
          return (
            <TableCell
              key={cell.id}
              className={cn(
                align === 'right' && 'tw:text-right',
                align === 'center' && 'tw:text-center',
                cell.column.columnDef.meta?.cellClassName,
              )}
              style={cell.column.getSize() !== 150 ? { width: cell.column.getSize() } : undefined}
              // Clicking the chevron toggles expansion only — never the row's onRowClick.
              onClick={isExpanderCell ? (e) => e.stopPropagation() : undefined}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
      {isExpanded && renderSubRow && (
        <TableRow className="tw:hover:bg-transparent">
          <TableCell colSpan={cells.length} className="tw:p-0">
            {renderSubRow(row.original)}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SortableDataRow<TData>({ row, onRowClick, getRowClassName }: DataRowProps<TData>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    animateLayoutChanges: () => false,
  });

  const cellStyle: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cells = row.getVisibleCells();

  return (
    <TableRow
      ref={setNodeRef}
      data-state={row.getIsSelected() ? 'selected' : undefined}
      onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
      className={cn(
        'tw:group/row',
        onRowClick && 'tw:cursor-pointer',
        isDragging && 'tw:cursor-grabbing',
        getRowClassName?.(row),
      )}
    >
      {cells.map((cell, cellIndex) => {
        const isReorderCell = cell.column.id === '__reorder';
        const isLast = cellIndex === cells.length - 1 || cells[cellIndex + 1]?.column.id === '__affordance';
        const metaAlign = cell.column.columnDef.meta?.align;
        const align = metaAlign ?? (isLast ? 'right' : 'left');
        const cellWidthStyle = cell.column.getSize() !== 150 ? { width: cell.column.getSize() } : undefined;
        return (
          <TableCell
            key={cell.id}
            className={cn(
              align === 'right' && 'tw:text-right',
              align === 'center' && 'tw:text-center',
              cell.column.columnDef.meta?.cellClassName,
            )}
            style={{ ...cellStyle, ...cellWidthStyle }}
            onClick={isReorderCell ? (e) => e.stopPropagation() : undefined}
          >
            {isReorderCell ? <ReorderHandle attributes={attributes} listeners={listeners} /> : renderCell(cell)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function renderCell<TData>(cell: Cell<TData, unknown>) {
  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

interface ReorderHandleProps {
  attributes: React.HTMLAttributes<HTMLButtonElement>;
  listeners: React.DOMAttributes<HTMLButtonElement> | undefined;
}

function ReorderHandle({ attributes, listeners }: ReorderHandleProps) {
  return (
    <button
      type="button"
      aria-label="Drag to reorder"
      className="tw:cursor-grab tw:text-muted-foreground tw:hover:text-foreground tw:touch-none tw:flex tw:items-center"
      {...attributes}
      {...listeners}
    >
      <GripVertical size={16} aria-hidden="true" />
    </button>
  );
}
